import { getIsQuickApplySchool } from "@nimble/candidate-api/quick-apply";
import { SchoolData } from "@nimble/candidate-api/types";
import { formatTestId } from "@nimble/ts-formatters/format-test-ids";
import { ReactNode } from "react";

import DistrictNameLink from "@/components/shared/school/school-details/district-name-link";
import SchoolDetailGrades from "@/components/shared/school/school-details/school-detail-grades";
import SchoolDetailLocation from "@/components/shared/school/school-details/school-detail-location";
import SchoolDetailPrincipal from "@/components/shared/school/school-details/school-detail-principal";
import SchoolDetailSalarySchedule from "@/components/shared/school/school-details/school-detail-salary-schedule";
import SchoolDetailStudentCount from "@/components/shared/school/school-details/school-detail-student-count";
import SchoolDetailTags from "@/components/shared/school/school-details/school-detail-tags";
import SchoolDetailType from "@/components/shared/school/school-details/school-detail-type";
import SchoolNameLink, {
  SchoolLinkTarget,
} from "@/components/shared/school/school-details/school-name-link";
import SchoolQuickApplyTag from "@/components/shared/school/school-details/school-quick-apply-tag";
import { schoolHeaderTestIds } from "@/lib/data-test-ids/schools.data-test-ids";
import { formatAddressForMaps } from "@/lib/helpers/format-address-for-maps";
import { StateConfig } from "@/lib/states-config";
import { getSalaryScheduleAttributionForState } from "@/lib/states-config";

export const InlineNameAndDistrict = ({
  schoolData,
  stateConfig,
  schoolLinkTarget,
}: {
  schoolData: SchoolData;
  stateConfig: StateConfig;
  schoolLinkTarget: SchoolLinkTarget;
}) => (
  <div>
    <h3
      className="text-base sm:text-lg font-bold text-foreground leading-tight inline"
      data-testid={schoolHeaderTestIds.SCHOOL_NAME}
    >
      <SchoolNameLink
        schoolData={schoolData}
        stateConfig={stateConfig}
        schoolLinkTarget={schoolLinkTarget}
      />
    </h3>
    <span
      className="text-xs sm:text-sm text-muted-foreground ml-2"
      data-testid={schoolHeaderTestIds.SCHOOL_DISTRICT}
    >
      <DistrictNameLink
        districtId={schoolData.district.nces_id}
        districtName={schoolData.district.name}
        stateConfig={stateConfig}
      />
    </span>
  </div>
);

export const StackedNameAndDistrict = ({
  schoolData,
  stateConfig,
  schoolLinkTarget,
}: {
  schoolData: SchoolData;
  stateConfig: StateConfig;
  schoolLinkTarget: SchoolLinkTarget;
}) => (
  <div className="mb-0.5">
    <h3
      className="text-base sm:text-lg md:text-sm font-bold text-foreground leading-tight md:leading-normal md:mb-0.5"
      data-testid={schoolHeaderTestIds.SCHOOL_NAME}
    >
      <SchoolNameLink
        schoolData={schoolData}
        stateConfig={stateConfig}
        schoolLinkTarget={schoolLinkTarget}
      />
    </h3>
    <p
      className="text-xs text-ink-subtle font-normal"
      data-testid={schoolHeaderTestIds.SCHOOL_DISTRICT}
    >
      <DistrictNameLink
        districtId={schoolData.district.nces_id}
        districtName={schoolData.district.name}
        stateConfig={stateConfig}
      />
    </p>
  </div>
);

export const LocationStat = ({ schoolData }: { schoolData: SchoolData }) => (
  <SchoolDetailLocation
    schoolId={schoolData.nces_id}
    city={schoolData.physical_address.city}
    state={schoolData.physical_address.state}
    distanceFromLocation={schoolData.distance_from_search_location_miles}
    directionsUrl={formatAddressForMaps(schoolData.physical_address)}
  />
);

export const LocationStatNoDistance = ({
  schoolData,
}: {
  schoolData: SchoolData;
}) => (
  <SchoolDetailLocation
    schoolId={schoolData.nces_id}
    city={schoolData.physical_address.city}
    state={schoolData.physical_address.state}
    directionsUrl={formatAddressForMaps(schoolData.physical_address)}
  />
);

export const PrincipalStat = ({ schoolData }: { schoolData: SchoolData }) => {
  const principal = schoolData.principal;
  if (!principal) return null;
  return (
    <SchoolDetailPrincipal
      schoolId={schoolData.nces_id}
      firstName={principal.first_name}
      lastName={principal.last_name}
    />
  );
};

export const StudentCountStat = ({
  schoolData,
}: {
  schoolData: SchoolData;
}) => {
  const studentCount = schoolData.school_metadata.student_count;
  if (studentCount === null || studentCount <= 0) return null;
  return (
    <SchoolDetailStudentCount
      schoolId={schoolData.nces_id}
      studentCount={studentCount}
    />
  );
};

export const SchoolTypeStat = ({ schoolData }: { schoolData: SchoolData }) => {
  const trimmed = schoolData.school_metadata.school_type?.trim();
  if (!trimmed) return null;
  return (
    <SchoolDetailType schoolId={schoolData.nces_id} schoolType={trimmed} />
  );
};

export const GradesStat = ({ schoolData }: { schoolData: SchoolData }) => {
  if (schoolData.grades.length === 0) return null;
  return (
    <SchoolDetailGrades
      schoolId={schoolData.nces_id}
      grades={schoolData.grades}
    />
  );
};

export const SalaryScheduleStat = ({
  schoolData,
}: {
  schoolData: SchoolData;
}) => {
  const link = schoolData.district.salary_schedule_link;
  if (!link) return null;
  return (
    <SchoolDetailSalarySchedule
      schoolId={schoolData.nces_id}
      salaryScheduleLink={link}
      attribution={getSalaryScheduleAttributionForState(
        schoolData.physical_address.state,
      )}
    />
  );
};

export const TagsRow = ({ schoolData }: { schoolData: SchoolData }) => {
  const isQuickApplicable = getIsQuickApplySchool(schoolData);
  if (schoolData.tags.length === 0 && !isQuickApplicable) return null;
  return (
    <SchoolDetailTags
      schoolId={schoolData.nces_id}
      tags={schoolData.tags}
      extraTags={isQuickApplicable ? <SchoolQuickApplyTag /> : null}
    />
  );
};

export const CardStatsRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-y-1.5 text-xs sm:text-sm text-ink-emphasis [&>*+*]:before:content-['•'] [&>*+*]:before:text-line-strong [&>*+*]:before:mr-2 [&>*+*]:before:hidden sm:[&>*+*]:before:inline">
    {children}
  </div>
);

export const GradesAndSalaryRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2 [&>*+*]:before:content-['•'] [&>*+*]:before:text-line-strong [&>*+*]:before:mr-2">
    {children}
  </div>
);

export const CardHeaderShell = ({
  schoolData,
  children,
}: {
  schoolData: SchoolData;
  children: ReactNode;
}) => (
  <div
    className="pb-2"
    data-testid={`${schoolHeaderTestIds.SCHOOL_HEADER}-${formatTestId(schoolData.name)}`}
  >
    <div className="min-w-0 flex flex-col gap-1 sm:gap-1.5">{children}</div>
  </div>
);
